import { createRouter } from '@tanstack/react-router'
import { notifyManager } from '@tanstack/react-query'
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query'
import { ConvexQueryClient } from '@convex-dev/react-query'
import { ConvexProviderWithAuth, ConvexReactClient } from 'convex/react'
import { ClerkProvider } from '@clerk/tanstack-react-start'
import { routeTree } from './routeTree.gen'
import { DefaultCatchBoundary } from './components/common/DefaultCatchBoundary'
import { LoadingScreen } from './components/common/LoadingScreen'
import { NotFound } from './components/NotFound'
import { createQueryClient } from './lib/queryClient'
import { useConvexClerkAuth } from '~/lib/useConvexClerkAuth'

export function getRouter() {
  if (typeof document !== 'undefined') {
    notifyManager.setScheduler(window.requestAnimationFrame)
  }

  const CONVEX_URL = (import.meta as any).env.VITE_CONVEX_URL!
  if (!CONVEX_URL) {
    console.error('missing envar CONVEX_URL')
  }
  
  // Create Convex clients - these are passed to route context for SSR auth
  const convexClient = new ConvexReactClient(CONVEX_URL)
  const convexQueryClient = new ConvexQueryClient(convexClient)

  // Create query client with Convex integration and enhanced defaults
  const queryClient = createQueryClient(convexQueryClient)

  const router = createRouter({
    routeTree,
    defaultPreload: 'intent',
    defaultPendingComponent: () => <LoadingScreen />,
    defaultErrorComponent: DefaultCatchBoundary,
    defaultNotFoundComponent: () => <NotFound />,
    // Pass all clients in context for SSR auth setup in __root.tsx
    context: { 
      queryClient,
      convexClient,
      convexQueryClient,
    },
    // InnerWrap runs inside RouterProvider, so router hooks are available
    InnerWrap: ({ children }) => (
      <ClerkProvider afterSignOutUrl="/">
        <ConvexProviderWithAuth client={convexClient} useAuth={useConvexClerkAuth}>
          {children}
        </ConvexProviderWithAuth>
      </ClerkProvider>
    ),
    scrollRestoration: true,
  })
  setupRouterSsrQueryIntegration({
    router,
    queryClient,
  })

  return router
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>
  }
  interface RouteContext {
    userId: string | null
  }
}
